import React, { useMemo, useState } from 'react';
import { Button, Card, Pill, Progress } from '../components/Primitives';
import { Icon } from '../components/Icon';
import { useApp } from '../lib/app-context';
import { canAccess, deterministicShuffle, dynamicLesson, knowledgeHealth } from '../lib/learning';
import { WORDS } from '../content/curriculum';

type SkillTab = 'listening' | 'reading' | 'pronunciation';
type RecognitionEvent = { results: { [index: number]: { [index: number]: { transcript: string } } } };
type RecognitionInstance = {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start: () => void;
};
type RecognitionConstructor = new () => RecognitionInstance;

function speak(text: string, rate = .9) {
  if (!('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'en-US';
  utterance.rate = rate;
  window.speechSynthesis.speak(utterance);
}

function normalize(value: string) {
  return value.toLowerCase().replace(/[’‘`]/g, "'").replace(/[^a-z0-9' ]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function recognitionConstructor(): RecognitionConstructor | null {
  const scope = window as unknown as { SpeechRecognition?: RecognitionConstructor; webkitSpeechRecognition?: RecognitionConstructor };
  return scope.SpeechRecognition ?? scope.webkitSpeechRecognition ?? null;
}

export function Skills() {
  const { state, answer, answerSkill, setRoute } = useApp();
  const allowed = canAccess(state, 'skills');
  const [tab, setTab] = useState<SkillTab>('listening');
  const [picked, setPicked] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const [listeningPlayed, setListeningPlayed] = useState(false);
  const [listeningStartedAt, setListeningStartedAt] = useState(0);
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [pronunciationResult, setPronunciationResult] = useState<'correct' | 'wrong' | 'unsupported' | null>(null);
  const lesson = dynamicLesson(state);
  const words = useMemo(() => deterministicShuffle([...lesson.newWords, ...lesson.reviewWords], `skills-${state.progress.day}-${state.progress.courseDate}`).slice(0, 12), [lesson.newWords, lesson.reviewWords, state.progress.day, state.progress.courseDate]);
  const current = words[index % Math.max(1, words.length)] ?? lesson.newWords[0];
  const health = knowledgeHealth(state);
  const options = useMemo(() => {
    if (!current) return [];
    const others = deterministicShuffle(WORDS.filter((word) => word.id !== current.id), `skills-pool-${current.id}`)
      .map((word) => word.uz)
      .filter((value, position, all) => all.indexOf(value) === position && value !== current.uz)
      .slice(0, 3);
    return deterministicShuffle([current.uz, ...others], `${tab}-${current.id}-${state.progress.day}`);
  }, [current, tab, state.progress.day]);

  if (!allowed) return <div className="screen"><Card className="locked-card"><Icon name="lock" size={38}/><h2>Skills Lab — PRO</h2><p>Listening, reading va pronunciation professional kursning PRO qismidir.</p><Button onClick={() => setRoute('pricing')}>PRO</Button></Card></div>;
  if (!current) return <div className="screen"><Card><h2>Skills kontenti topilmadi</h2><Button onClick={() => setRoute('dashboard')}>Bosh sahifa</Button></Card></div>;

  const resetAttempt = () => {
    setPicked(null);
    setListeningPlayed(false);
    setListeningStartedAt(0);
    setTranscript('');
    setPronunciationResult(null);
    setRecording(false);
  };
  const advance = () => { resetAttempt(); setIndex((value) => value + 1); };
  const choose = (option: string) => {
    if (picked) return;
    setPicked(option);
    const ok = option === current.uz;
    const evidence = tab === 'listening' ? 'listening' : 'context';
    answer(current, ok, listeningStartedAt ? Date.now() - listeningStartedAt : 0, evidence);
    answerSkill(tab === 'listening' ? 'listening' : 'reading', ok, current.id);
  };
  const playListening = () => {
    setListeningPlayed(true);
    setListeningStartedAt(Date.now());
    speak(current.exampleEn, .88);
  };
  const startPronunciation = () => {
    const Ctor = recognitionConstructor();
    if (!Ctor) {
      setPronunciationResult('unsupported');
      return;
    }
    const recognition = new Ctor();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    setRecording(true);
    setTranscript('');
    setPronunciationResult(null);
    recognition.onresult = (event) => {
      const heard = String(event.results?.[0]?.[0]?.transcript ?? '');
      const heardNormalized = normalize(heard);
      const target = normalize(current.en);
      const ok = heardNormalized === target || heardNormalized.split(' ').includes(target);
      setTranscript(heard);
      setPronunciationResult(ok ? 'correct' : 'wrong');
      answer(current, ok, 0, 'production');
      answerSkill('pronunciation', ok, current.id);
    };
    recognition.onerror = () => {
      setPronunciationResult('wrong');
    };
    recognition.onend = () => setRecording(false);
    recognition.start();
  };

  return <div className="screen">
    <div className="screen-head"><div><span className="eyebrow">FOUR STRANDS · REAL EVIDENCE</span><h1>Skills Lab</h1><p>Eshitish, kontekstdan tushunish va talaffuz alohida evidence sifatida mashq qilinadi.</p></div><div className="segmented"><button className={tab === 'listening' ? 'active' : ''} onClick={() => { setTab('listening'); resetAttempt(); }}>Listening</button><button className={tab === 'reading' ? 'active' : ''} onClick={() => { setTab('reading'); resetAttempt(); }}>Reading</button><button className={tab === 'pronunciation' ? 'active' : ''} onClick={() => { setTab('pronunciation'); resetAttempt(); }}>Pronunciation</button></div></div>
    <div className="metric-grid"><div className="metric"><span className="metric-icon"><Icon name="headphones"/></span><div><strong>{health.listening}%</strong><span>Listening</span></div></div><div className="metric"><span className="metric-icon"><Icon name="book"/></span><div><strong>{health.reading}%</strong><span>Reading</span></div></div><div className="metric"><span className="metric-icon"><Icon name="spark"/></span><div><strong>{health.pronunciation}%</strong><span>Pronunciation</span></div></div></div>

    {tab === 'listening' && <Card className="lesson-stage quiz-stage"><Pill tone="blue">LISTEN → RETRIEVE</Pill><h2>Gapni matnsiz eshiting, keyin asosiy so‘z ma’nosini tanlang.</h2><button className="audio-button" onClick={playListening}><Icon name="headphones" size={28}/></button>{!listeningPlayed ? <p className="muted">Avval audioni eshiting. Variantlar keyin ochiladi.</p> : <div className="quiz-options">{options.map((option) => { const cls = picked ? option === current.uz ? 'correct' : option === picked ? 'wrong' : '' : ''; return <button key={option} className={cls} disabled={Boolean(picked)} onClick={() => choose(option)}>{option}{cls === 'correct' && <Icon name="check"/>}</button>; })}</div>}{picked && <div className="example-box"><strong>{current.exampleEn}</strong><span>{current.exampleUz}</span><Button onClick={advance}>Keyingi</Button></div>}</Card>}

    {tab === 'reading' && <Card className="lesson-stage quiz-stage"><Pill tone="green">READ → INFER</Pill><div className="reading-passage"><p>{current.exampleEn}</p></div><p className="muted">Gapning umumiy ma’nosidan foydalaning; har bir so‘zni alohida tarjima qilishga urinmang.</p><h2>“{current.en}” bu kontekstda nimani anglatadi?</h2><div className="quiz-options">{options.map((option) => { const cls = picked ? option === current.uz ? 'correct' : option === picked ? 'wrong' : '' : ''; return <button key={option} className={cls} disabled={Boolean(picked)} onClick={() => choose(option)}>{option}</button>; })}</div>{picked && <Button onClick={advance}>Keyingi matn</Button>}</Card>}

    {tab === 'pronunciation' && <Card className="lesson-stage word-stage"><Pill tone="gold">LISTEN → SPEAK → VERIFY</Pill><h1>{current.en}</h1><p>{current.exampleEn}</p><div className="daily-actions"><Button onClick={() => speak(current.en, .72)}><Icon name="headphones"/> Sekin eshitish</Button><Button variant="secondary" onClick={() => speak(current.exampleEn, .95)}>Natural gap</Button></div><div className="example-box"><strong>Modelni eshiting, keyin mikrofon orqali so‘zni ayting.</strong><span>Speech Recognition mavjud bo‘lsa transcript real ravishda tekshiriladi. Qo‘llab-quvvatlanmasa soxta ball berilmaydi.</span></div><Button disabled={recording} onClick={startPronunciation}><Icon name="headphones"/>{recording ? 'Tinglanmoqda...' : 'Mikrofon bilan tekshirish'}</Button>{pronunciationResult === 'unsupported' && <div className="status-message">Bu brauzer Speech Recognition’ni qo‘llamaydi. Eshitib-shadowing mashqini bajaring; avtomatik mastery o‘zgartirilmaydi.</div>}{pronunciationResult && pronunciationResult !== 'unsupported' && <div className={`production-feedback ${pronunciationResult}`}><strong>{pronunciationResult === 'correct' ? 'Transcript target bilan mos' : 'Yana bir marta aniqroq ayting'}</strong><span>{transcript ? `Eshitildi: ${transcript}` : 'Ovoz aniqlanmadi.'}</span></div>}<div className="lesson-nav">{pronunciationResult === 'wrong' && <Button variant="secondary" onClick={startPronunciation}>Qayta urinish</Button>}{pronunciationResult && <Button onClick={advance}>Keyingi</Button>}</div></Card>}

    <Card><span className="eyebrow">PROGRESS</span><Progress value={tab === 'listening' ? health.listening : tab === 'reading' ? health.reading : health.pronunciation} label={`${tab} mastery`}/><Button variant="ghost" onClick={() => setRoute('lesson')}>Daily Missionga qaytish</Button></Card>
  </div>;
}
